import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

import { CostFormatPipe } from '@app/core/_base/layout/pipes/cost-format.pipe';
import { environment } from '@app/env-service';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

@Injectable({
  providedIn: 'root',
})
export class ExportTemplateRfqService {
  clientConfig :any = environment.clientConfig;

  url :string = environment.url;
  urlV2 :string = environment.urlV2;

  excelFileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  excelFileExtension = '.xlsx';

  curPipe :CurrencyPipe;
  datePipe :DatePipe;
  costFormat :CostFormatPipe;

  private readonly _templates = new BehaviorSubject<any>(<any>[]);
  public templates$ = this._templates.asObservable();

  private readonly _activeRecord = new BehaviorSubject<any>(<any>{});
  public activeRecord$ = this._activeRecord.asObservable();

  public gettingRecords :boolean = false;

  private params :any = {
    per_page: 20,
    page: 1,
  };

  constructor(private http :HttpClient) {
    this.curPipe = new CurrencyPipe('en');
    this.datePipe = new DatePipe('en');
    this.costFormat = new CostFormatPipe();
  }

  getExportTemplateRfqs(params) {
    this.gettingRecords = true;

    return this.http
      .get(`${this.urlV2}/manage/export-template`, { params: params })
      .subscribe((resp :any) => {
        this.gettingRecords = false;
        this._templates.next(resp);
      });
  }

  getById(templateId :number) :Observable<any> {
    return this.http.get(`${this.urlV2}/manage/export-template/${templateId}`);
  }

  searchTemplate(params :any) :Observable<any> {
    return this.http.get(`${this.urlV2}/manage/export-template`, {
      params: params,
    });
  }

  refreshList() {
    this.getExportTemplateRfqs(this.params);
  }

  setParams(params :any) {
    this.params = params;
  }

  saveTemplate(payload :any) :Observable<any> {
    return this.http.post(`${this.urlV2}/manage/export-template`, payload).pipe(
      tap((resp :any) => {
        setTimeout(() => {
          const templates :any = this._templates.getValue();
          if (templates && templates.data) {
            templates.data.splice(0, 0, resp);
            this._templates.next(templates);
          }
        });
      }),
    );
  }

  updateTemplate(payload :any, templateId :number) {
    return this.http
      .put(`${this.urlV2}/manage/export-template/${templateId}`, payload)
      .pipe(
        tap((resp :any) => {
          this.tryUpdateOrAddResp(resp);
        }),
      );
  }

  deleteTemplate(templateId :number) {
    return this.http
      .delete(`${this.urlV2}/manage/export-template/${templateId}`)
      .pipe(
        tap((resp :any) => {
          const templates :any = this._templates.getValue();
          const templateIndex = templates.data.findIndex(
            (row) => row.id == templateId,
          );
          templates.data.splice(templateIndex, 1);
          this._templates.next(templates);
        }),
      );
  }

  setActiveById(id :number) {
    this.gettingRecords = true;

    const record :any = this._templates.getValue();
    if (record.data) {
      const template = record.data.find((row) => {
        return row.id == id;
      });
      if (template) {
        this.setActive(template);

        return;
      }
    }
    this.getById(id).subscribe((template) => {
      this.setActive(template);
    });
  }

  setActive(po :any) {
    this.gettingRecords = false;

    this._activeRecord.next(po);
  }

  getActive() {
    return this._activeRecord.getValue();
  }

  tryUpdateOrAddResp(resp) {
    const templates :any = this._templates.getValue();
    if (!templates || !templates.data) {
      return;
    }
    const templateIndex = templates.data.findIndex((row) => row.id == resp.id);

    templates.data[templateIndex] = resp;

    this._templates.next(templates);
  }

  private getParseableData(value :string) :any {
    try {
      return value ? JSON.parse(value) : [];
    } catch (error) {
      return [value];
    }
  }

  private getDimensionStringFormat(dimension :{
    length :number;
    width :number;
    height :number;
  }) :string {
    const { length, width, height } = dimension;

    return `${length ? length : 0}x${width ? width : 0}x${height ? height : 0}`;
  }

  private downloadExelFile(
    recordSheet :XLSX.WorkSheet,
    filename :string,
    type :string,
  ) :void {
    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
    });

    this.saveFile(excelBuffer, filename, type);
  }

  private saveFile(buffer :any, fileName :string, ext ?:string) :void {
    const data :Blob = new Blob([buffer], { type: this.excelFileType });
    FileSaver.saveAs(data, fileName + (ext ? ext : this.excelFileExtension));
  }

  transformData(rfqs :any[], template :any) :any {
    const masterSkuData :any = [];

    const exportFields = template.fields;

    if (exportFields.includes('Master SKU #')) {
      const fieldIdx = exportFields.indexOf('Master SKU #');
      exportFields.splice(fieldIdx, 1);

      exportFields.splice(0, 0, 'Master SKU #');
    } else {
      exportFields.splice(0, 0, 'Master SKU #');
    }

    if (exportFields.includes('Variant SKU #')) {
      const fieldIdx = exportFields.indexOf('Variant SKU #');
      exportFields.splice(fieldIdx, 1);

      exportFields.splice(1, 0, 'Variant SKU #');
    } else {
      exportFields.splice(1, 0, 'Variant SKU #');
    }

    let rfqList :any[] = [];
    rfqs.forEach((q) => {
      rfqList = rfqList.concat(q);
    });

    rfqList.forEach((sku, key) => {
      const boxWeightArr = this.getParseableData(sku.box_weight);
      const productWeightArr = this.getParseableData(sku.product_weight);
      const shippingWeightArr = this.getParseableData(sku.shipping_weight);
      const cartonWeightArr = this.getParseableData(sku.carton_weight);
      const palletWeightArr = this.getParseableData(sku.pallet_weight);
      const productLinkArr = this.getParseableData(sku.product_link);

      let boxWeightStr = '';
      let productWeightStr = '';
      let shippingWeightStr = '';
      let cartonWeightStr = '';
      let palletWeightStr = '';
      let productLinkStr = '';

      if (boxWeightArr && typeof boxWeightArr === 'object') {
        boxWeightStr = boxWeightArr.reduce(
          (acc, value) =>
            acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
          '',
        );
      }

      if (productWeightArr && typeof productWeightArr === 'object') {
        productWeightStr = productWeightArr.reduce(
          (acc, value) =>
            acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
          '',
        );
      }

      if (shippingWeightArr && typeof shippingWeightArr === 'object') {
        shippingWeightStr = shippingWeightArr.reduce(
          (acc, value) =>
            acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
          '',
        );
      }

      if (cartonWeightArr && typeof cartonWeightArr === 'object') {
        cartonWeightStr = cartonWeightArr.reduce(
          (acc, value) =>
            acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
          '',
        );
      }

      if (palletWeightArr && typeof palletWeightArr === 'object') {
        palletWeightStr = palletWeightArr.reduce(
          (acc, value) =>
            acc ? `${acc}, ${value.wv}${value.mu}` : `${value.wv}${value.mu}`,
          '',
        );
      }

      if (
        productLinkArr &&
        typeof productLinkArr === 'object' &&
        productLinkArr.length
      ) {
        productLinkStr = productLinkArr.reduce(
          (acc, value) => (acc ? `${acc}, ${value.lv}` : value.lv),
          '',
        );
      }

      const master_sku =
        sku.variant_parent_id == 0 ? sku?.internal_sku : sku.parent?.internal_sku;
      const variant_sku = sku.variant_parent_id == 0 ? '' : sku?.internal_sku;

      const locations = [];
      const warehouses = [];
      const alternate_skus = [];
      const tags = [];

      sku.location_stocks?.forEach((stockLocation) => {
        if (stockLocation.stock_locations !== null) {
          stockLocation.stock_locations?.forEach((loc) => {
            if (!locations.includes(loc.location_name)) {
              locations.push(loc.location_name);
            }
          });
        }
        if (stockLocation.warehouse !== null) {
          if (!warehouses.includes(stockLocation.warehouse.location_name)) {
            warehouses.push(stockLocation.warehouse.location_name);
          }
        }
      });

      sku.alternate_skus?.forEach((alt) => {
        alternate_skus.push(alt.alternate_sku);
      });

      sku.tags?.forEach((tag) => {
        if (tag.tag_details != null) {
          tags.push(tag.tag_details.name);
        }
      });

      const pushData = {};

      if (exportFields.includes('Master SKU #')) {
        pushData['Master SKU #'] = master_sku;
      }

      if (exportFields.includes('Variant SKU #')) {
        pushData['Variant SKU #'] = variant_sku;
      }

      if (exportFields.includes('Product Name')) {
        pushData['Product Name'] = sku.product_name;
      }

      if (exportFields.includes('Product URL')) {
        pushData['Product URL'] = `${window.location.origin}/quotes/${sku.id}/view`;
      }

      if (exportFields.includes('Alternate SKUs')) {
        pushData['Alternate SKUs'] = alternate_skus.join(', ');
      }

      if (exportFields.includes('UPC')) {
        pushData['UPC'] = sku.upc ? sku.upc : '';
      }

      if (exportFields.includes('Location Warehouse')) {
        pushData['Location Warehouse'] = warehouses.join(', ');
      }

      if (exportFields.includes('Locations')) {
        pushData['Locations'] = locations.join(', ');
      }

      if (exportFields.includes('Unit Cost (FIFO)')) {
        pushData['Unit Cost (FIFO)'] = this.costFormat.transform(
          sku?.base_price?.weighted,
        );
      }

      if (exportFields.includes('Wholesale Cost')) {
        pushData['Wholesale Cost'] = this.costFormat.transform(
          sku?.wholesale_price,
        );
      }

      if (exportFields.includes('Retail Cost')) {
        pushData['Retail Cost'] = this.costFormat.transform(sku?.retail_price);
      }

      if (exportFields.includes('Qty On Hand')) {
        pushData['Qty On Hand'] = sku?.stock?.qty_onhand;
      }

      if (exportFields.includes('Qty Incoming')) {
        pushData['Qty Incoming'] = sku?.stock?.qty_incoming;
      }

      if (exportFields.includes('Qty Available')) {
        pushData['Qty Available'] =
          sku?.stock?.qty_onhand - sku?.stock?.qty_pending;
      }

      if (exportFields.includes('Qty Pending')) {
        pushData['Qty Pending'] = sku?.stock?.qty_pending;
      }

      if (exportFields.includes('Re-Order Level')) {
        pushData['Re-Order Level'] = sku.reorder_level;
      }

      if (exportFields.includes('Low Stock Alert Quantity')) {
        pushData['Low Stock Alert Quantity'] = sku.qty_limit_notify;
      }

      if (exportFields.includes('Minimum Stock')) {
        pushData['Minimum Stock'] = sku.oss_limit_notify;
      }

      if (exportFields.includes('Last Month Sale Qty')) {
        pushData['Last Month Sale Qty'] =
          sku.last_month_sale != null ? sku.last_month_sale.quantity : 0;
      }

      if (exportFields.includes('Product Description')) {
        pushData['Product Description'] = this.removeTagsOnString(
          sku.product_description,
        );
      }

      if (exportFields.includes('Product Category')) {
        pushData['Product Category'] = sku.product_category
          ? sku.product_category.product_category
          : ' ';
      }

      if (exportFields.includes('Product Sub Category')) {
        pushData['Product Sub Category'] = sku.product_sub_category
          ? sku.product_sub_category.sub_category
          : ' ';
      }

      if (exportFields.includes('Preferred Vendor')) {
        pushData['Preferred Vendor'] = sku.preferred_vendor;
      }

      if (exportFields.includes('Product Tags')) {
        pushData['Product Tags'] = tags.join(', ');
      }

      if (exportFields.includes('Product Weight')) {
        pushData['Product Weight'] = productWeightStr;
      }

      if (exportFields.includes('Product Dimensions')) {
        pushData['Product Dimensions'] = this.getDimensionStringFormat({
          length: sku.product_length,
          width: sku.product_width,
          height: sku.product_height,
        });
      }

      if (exportFields.includes('Shipping Weight')) {
        pushData['Shipping Weight'] = shippingWeightStr;
      }

      if (exportFields.includes('Shipping Dimensions')) {
        pushData['Shipping Dimensions'] = this.getDimensionStringFormat({
          length: sku.shipping_length,
          width: sku.shipping_width,
          height: sku.shipping_height,
        });
      }

      if (exportFields.includes('Carton Weight')) {
        pushData['Carton Weight'] = cartonWeightStr;
      }

      if (exportFields.includes('Carton Dimensions')) {
        pushData['Carton Dimensions'] = this.getDimensionStringFormat({
          length: sku.carton_length,
          width: sku.carton_width,
          height: sku.carton_height,
        });
      }

      if (exportFields.includes('Box Weight')) {
        pushData['Box Weight'] = boxWeightStr;
      }

      if (exportFields.includes('Box Dimensions')) {
        pushData['Box Dimensions'] = this.getDimensionStringFormat({
          length: sku.box_length,
          width: sku.box_width,
          height: sku.box_height,
        });
      }

      if (exportFields.includes('Pallet Weight')) {
        pushData['Pallet Weight'] = palletWeightStr;
      }

      if (exportFields.includes('Pallet Dimensions')) {
        pushData['Pallet Dimensions'] = this.getDimensionStringFormat({
          length: sku.pallet_length,
          width: sku.pallet_width,
          height: sku.pallet_height,
        });
      }

      if (exportFields.includes('Cartons per pallet')) {
        pushData['Cartons per pallet'] = sku.cartons_per_pallet;
      }

      if (exportFields.includes('Units per pallet')) {
        pushData['Units per pallet'] = sku.units_per_pallet;
      }

      if (exportFields.includes('Units per carton')) {
        pushData['Units per carton'] = sku.units_per_carton;
      }

      if (exportFields.includes('Product Links')) {
        pushData['Product Links'] = productLinkStr;
      }

      if (exportFields.includes('Materials')) {
        pushData['Materials'] = this.getParseableData(sku.materials).reduce(
          (acc, value) => (acc ? `${acc}, ${value.mv}` : value.mv),
          '',
        );
      }

      if (exportFields.includes('Link to Product Photo')) {
        pushData['Link to Product Photo'] = sku.photo;
      }

      if (exportFields.includes('Link to Product Design')) {
        pushData['Link to Product Design'] = sku.product_design;
      }

      if (exportFields.includes('Link to Product Design')) {
        pushData['Link to Product Design'] = sku.product_design;
      }

      if (exportFields.includes('Default Receiving Bin')) {
        pushData['Default Receiving Bin'] =
          sku?.default_receiving_bin?.location_name;
      }

      if (exportFields.includes('Product Image')) {
        pushData['Product Image'] = sku.photo;
      }

      masterSkuData.push(pushData);
    });

    return masterSkuData;
  }

  exportTemplateToPdf(rfqs :any[], template :any) :void {
    const rows = this.transformData(rfqs, template);
    this.getImgDataUrlValue(
      environment.clientConfig.assetImgDir + 'logo-print.png',
      (imgUri :any) => {
        this.generateRfqsPdf(rows, imgUri, template);
      },
    );
  }

  exportTemplateToCsv(rfqs :any[], template :any) :void {
    const filename :string = template.name;

    const rows = this.transformData(rfqs, template);

    if (!rfqs || !rfqs.length) {
      return;
    }
    const separator = ',';

    const keys = Object.keys(rows[0]);

    const csvContent =
      keys.join(separator) +
      '\n' +
      rows
        .map((row) => {
          return keys
            .map((k) => {
              let cell = row[k] === null || row[k] === undefined ? '' : row[k];
              cell =
                cell instanceof Date
                  ? cell.toLocaleString()
                  : cell.toString().replace(/"/g, '""');
              if (cell.search(/("|,|\n)/g) >= 0) {
                cell = `"${cell}"`;
              }

              return cell;
            })
            .join(separator);
        })
        .join('\n');
    this.saveFile(csvContent, filename, '.csv');
    Swal.close();
  }

  exportTemplateToExcel(rfqs :any[], template :any) :void {
    const filename :string = template.name;

    const masterSkuData = this.transformData(rfqs, template);

    const masterSkusRecordSheet :XLSX.WorkSheet =
      XLSX.utils.json_to_sheet(masterSkuData);
    this.downloadExelFile(masterSkusRecordSheet, filename, '.xlsx');
    Swal.close();
  }

  getImgDataUrlValue(imgUrl :string, callback) :void {
    const xhr = new XMLHttpRequest();
    xhr.onload = function () {
      const reader = new FileReader();
      reader.onloadend = function () {
        callback(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', imgUrl);
    xhr.responseType = 'blob';
    xhr.send();
  }

  creatRfqsPDF(rfqs :any, logoUri, template) :void {
    const filename :string = template.name;

    const fields = template.fields;

    const headers = [];

    for (var i = 0, n = fields.length; i < n; i++) {
      headers[i] = {
        text: fields[i],
        bold: true,
      };
    }

    const tableData :any[] = [headers];

    rfqs.forEach((rfq) => {
      const data = [];

      fields.forEach((field :string) => {
        data.push({ text: rfq[field] ? rfq[field] : '' });
      });

      tableData.push(data);
    });

    const today = new Date();

    const headerContent = [
      {
        columns: [
          {
            alignment: 'left',
            image: logoUri,
            width: 200,
            style: 'logo',
          },
          [
            {
              text: 'Request for Quotes',
              alignment: 'right',
              style: 'header',
            },
            {
              text: 'Date : ' + this.datePipe.transform(today, 'mediumDate'),
              alignment: 'right',
            },
          ],
        ],
      },
    ];

    const widthArray :any[] = [];

    for (var i = 0, n = fields.length; i < n; i++) {
      widthArray[i] = 'auto';
    }

    const widths :any[] = widthArray;

    const tableContent :any[] = [
      {
        style: 'table',
        table: {
          widths: widths,
          headerRows: 1,
          body: tableData,
        },
      },
    ];

    let pdfContent :any[] = [];
    pdfContent = pdfContent.concat(headerContent, tableContent);

    const documentDefinition = {
      pageSize: 'legal',
      pageOrientation: 'landscape',

      content: pdfContent,

      defaultStyle: {
        fontSize: 10,
        columnGap: 20,
      },
      styles: {
        companyheader: {
          fontSize: 22,
          bold: true,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        summarytitle: {
          fontSize: 14,
          bold: true,
        },
        header: {
          fontSize: 20,
          bold: true,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        subheader: {
          fontSize: 14,
          bold: false,
          margin: [0, 0, 0, 0],
          padding: [0, 0, 0, 0],
        },
        summary: {
          fontSize: 14,
          bold: false,
          margin: [10, 10, 10, 10],
          padding: [10, 10, 10, 10],
        },
        gmvTable: {
          margin: [0, 5, 0, 15],
        },
        tableHeader: {
          bold: true,
          fontSize: 13,
          color: 'black',
        },
      },
    };
    const file = filename + '.pdf';
    pdfMake.createPdf(documentDefinition).download(file);
    Swal.close();
  }

  generateRfqsPdf(
    rfqs :any[],
    logoUri :string,
    template :{ name :string; fields :string[] },
  ) :void {
    const filename :string = template.name;
    const fields :string[] = template.fields;
    const headers :any[] = fields.map((field) => ({ text: field, bold: true }));
    const totalImages = rfqs.filter(
      (rfq) => fields.includes('Product Image') && rfq['Product Image'],
    ).length;
    let imagesProcessed = 0;

    const createPdf = () :void => {
      const today = new Date();
      const headerContent :any[] = [
        {
          columns: [
            { alignment: 'left', image: logoUri, width: 200 },
            [
              { text: 'Request for Quotes', alignment: 'right', style: 'header' },
              {
                text: `Date: ${new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(today)}`,
                alignment: 'right',
              },
            ],
          ],
        },
      ];

      const columnWidths :string[] = fields.map(() => 'auto');
      const tableData :any[] = [headers];

      rfqs.forEach((rfq) => {
        const rowData :any[] = fields.map((field) => {
          if (field === 'Product Image' && rfq[field]) {
            return { image: rfq[field], fit: [80, 50], alignment: 'center' };
          } else {
            return { text: rfq[field] || '' };
          }
        });

        tableData.push(rowData);
      });

      const documentDefinition :any = {
        pageSize: 'legal',
        pageOrientation: 'landscape',
        content: [
          ...headerContent,
          {
            style: 'table',
            table: { widths: columnWidths, headerRows: 1, body: tableData },
          },
        ],
        defaultStyle: { fontSize: 10 },
        styles: {
          header: { fontSize: 20, bold: true },
          tableHeader: { bold: true, fontSize: 13, color: 'black' },
        },
      };

      pdfMake.createPdf(documentDefinition).download(`${filename}.pdf`);
      Swal.close();
    };

    rfqs.forEach((rfq) => {
      if (fields.includes('Product Image') && rfq['Product Image']) {
        this.getImgDataUrlValue(rfq['Product Image'], (base64Image :string) => {
          rfq['Product Image'] = base64Image;
          imagesProcessed++;
          if (imagesProcessed === totalImages) {
            createPdf();
          }
        });
      }
    });

    if (totalImages === 0) {
      createPdf();
    }
  }

  private removeTagsOnString(inputString :string = '') :string {
    if (!inputString) {
      return '';
    }
    const div = document.createElement('div');
    div.innerHTML = inputString.replace(/<br\s*[\/]?>/gi, '\n');

    return div.textContent || div.innerText || '';
  }
}
